import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBible, FaBars, FaSearch, FaHome } from 'react-icons/fa';

import bibleLogo from '../../assets/images/bibleTamil.png';
import bibleEnglishLogo from '../../assets/images/bibleEnglish.png';
import bibleDeutschLogo from '../../assets/images/bibleDeutsch.png';

import './TopHeader.css';
import { AuthContext } from '../Authentication/AuthContext';
import {
  getBibleDownloadCacheSnapshot,
  startBibleDownloadCache,
  subscribeBibleDownloadCache,
} from '../Bible/DownloadCache';

function TopHeader() {
  const { language, setLanguage } = useContext(AuthContext);
  const location = useLocation();
  const languageDropdownRef = useRef(null);

  const languageOptions = useMemo(() => ['English', 'Deutsch', 'தமிழ்'], []);
  const currentLanguage = languageOptions.includes(language) ? language : 'தமிழ்';

  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [downloadCacheStatus, setDownloadCacheStatus] = useState(() =>
    getBibleDownloadCacheSnapshot()
  );

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = subscribeBibleDownloadCache((nextStatus) => {
      setDownloadCacheStatus(nextStatus);
    });

    startBibleDownloadCache();

    return unsubscribe;
  }, []);

  useEffect(() => {
    const closeLanguageDropdown = (event) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', closeLanguageDropdown);

    return () => {
      document.removeEventListener('mousedown', closeLanguageDropdown);
    };
  }, []);

  const orderedLanguageOptions = useMemo(
    () => [
      currentLanguage,
      ...languageOptions.filter((option) => option !== currentLanguage),
    ],
    [currentLanguage, languageOptions]
  );

  const menuItems = useMemo(() => {
    if (currentLanguage === 'English') {
      return {
        home: 'Home',
        bible: 'Bible',
        verses: 'Verses',
        words: 'Words',
      };
    }

    if (currentLanguage === 'Deutsch') {
      return {
        home: 'Zuhause',
        bible: 'Bibel',
        verses: 'Verse',
        words: 'Wörter',
      };
    }

    return {
      home: 'முகப்பு',
      bible: 'வேதாகமம்',
      verses: 'வசனங்கள்',
      words: 'சொற்கள்',
    };
  }, [currentLanguage]);

  const selectedLanguageText = useMemo(() => {
    if (currentLanguage === 'English') {
      return 'Language';
    }

    if (currentLanguage === 'Deutsch') {
      return 'Sprache';
    }

    return 'மொழி';
  }, [currentLanguage]);

  const brandText = useMemo(() => {
    if (currentLanguage === 'English') {
      return 'Holy Bible';
    }

    if (currentLanguage === 'Deutsch') {
      return 'Heilige Bibel';
    }

    return 'பரிசுத்த வேதாகமம்';
  }, [currentLanguage]);

  const brandSubtitle = useMemo(() => {
    if (currentLanguage === 'English') {
      return 'John 1 : 5 · And the light shineth in darkness';
    }

    if (currentLanguage === 'Deutsch') {
      return 'Johannes 1 : 5 · Das Licht scheint in der Finsternis';
    }

    return 'யோவான் 1 : 5 · ஒளி இருளிலே பிரகாசிக்கிறது';
  }, [currentLanguage]);

  const currentLogo = useMemo(() => {
    if (currentLanguage === 'English') {
      return bibleEnglishLogo;
    }

    if (currentLanguage === 'Deutsch') {
      return bibleDeutschLogo;
    }

    return bibleLogo;
  }, [currentLanguage]);

  const logoAltText = useMemo(() => {
    if (currentLanguage === 'English') {
      return 'Holy Bible English Logo';
    }

    if (currentLanguage === 'Deutsch') {
      return 'Holy Bible Deutsch Logo';
    }

    return 'பரிசுத்த வேதாகமம் Logo';
  }, [currentLanguage]);

  const navigationItems = useMemo(
    () => [
      {
        path: '/home',
        label: menuItems.home,
        icon: <FaHome />,
      },
      {
        path: '/bible',
        label: menuItems.bible,
        icon: <FaBible />,
      },
      {
        path: '/bible-verse-find',
        label: menuItems.verses,
        icon: <FaSearch />,
      },
      {
        path: '/search-verse-by-word',
        label: menuItems.words,
        icon: <FaSearch />,
      },
    ],
    [menuItems]
  );

  const activeMenuLabel =
    navigationItems.find((item) => item.path === location.pathname)?.label ||
    menuItems.home;

  const handleLanguageChange = (selectedLanguage) => {
    setLanguage(selectedLanguage);
    localStorage.setItem('language', selectedLanguage);
    setLanguageOpen(false);
  };

  const handleLanguageKeyDown = (event, selectedLanguage) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleLanguageChange(selectedLanguage);
    }

    if (event.key === 'Escape') {
      setLanguageOpen(false);
    }
  };

  const getCacheStatusLabel = () => {
    if (downloadCacheStatus.status === 'ready') {
      return 'Ready';
    }

    if (downloadCacheStatus.status === 'ready_with_errors') {
      return 'Ready';
    }

    if (downloadCacheStatus.status === 'running') {
      return 'Downloading';
    }

    if (downloadCacheStatus.status === 'checking') {
      return 'Checking';
    }

    if (downloadCacheStatus.status === 'error') {
      return 'Error';
    }

    return 'Waiting';
  };

  return (
    <div className="top-header-shell">
      <header className="top-header">
        <div className="top-header-particles" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>

        <div className="logo-menu-container">
          <div className="logo-container">
            <Link to="/home" className="logo-link" aria-label={brandText}>
              <img src={currentLogo} alt={logoAltText} className="logo" />

              <span className="logo-text-stack">
                <span className="brand-title">{brandText}</span>
                <span className="brand-subtitle">{brandSubtitle}</span>
              </span>
            </Link>
          </div>

          <nav className={`menu ${menuOpen ? 'open' : ''}`} aria-label="Main menu">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`menu-item ${
                  location.pathname === item.path ? 'active' : ''
                }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="header-actions">
            <div
              className={`header-cache-status-badge status-${downloadCacheStatus.status}`}
              title={`${getCacheStatusLabel()} ${downloadCacheStatus.percent}%`}
            >
              <span className="header-cache-dot" />
              <span className="header-cache-label">{getCacheStatusLabel()}</span>
              <span className="header-cache-percent">
                {downloadCacheStatus.percent}%
              </span>
            </div>

            <button
              type="button"
              className="menu-toggle"
              onClick={() => setMenuOpen((previousValue) => !previousValue)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <span className="selected-menu">{activeMenuLabel}</span>
              <FaBars size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="contact-info">
        <div className="social-icons">
          <Link to="/privacypolicy" className="privacy-policy-link">
            Privacy Policy
          </Link>

          <Link to="/support" className="support-link">
            Support
          </Link>
        </div>

        <div className="language-selector" ref={languageDropdownRef}>
          <label id="language-selector-label">{selectedLanguageText}:</label>

          <div className={`language-dropdown ${languageOpen ? 'open' : ''}`}>
            <button
              type="button"
              className="language-select-button"
              aria-haspopup="listbox"
              aria-expanded={languageOpen}
              aria-labelledby="language-selector-label"
              onClick={() =>
                setLanguageOpen((previousValue) => !previousValue)
              }
            >
              <span className="language-selected-value">{currentLanguage}</span>
              <span className="language-arrow" aria-hidden="true" />
            </button>

            {languageOpen && (
              <ul className="language-options" role="listbox">
                {orderedLanguageOptions.map((option, index) => (
                  <li
                    key={option}
                    role="option"
                    aria-selected={currentLanguage === option}
                    tabIndex={0}
                    className={`language-option ${
                      currentLanguage === option ? 'active' : ''
                    } ${index === 0 ? 'top-option' : ''}`}
                    onClick={() => handleLanguageChange(option)}
                    onKeyDown={(event) =>
                      handleLanguageKeyDown(event, option)
                    }
                  >
                    <span>{option}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopHeader;